import { canonicalUrlFor } from "@/lib/seo/canonical-overrides";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft, Clock } from "lucide-react";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { ArticleBody } from "@/components/site/ArticleBody";
import { ArticleCard } from "@/components/site/ArticleCard";
import { getBlogPost } from "@/lib/blog.functions";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { AuthorByline } from "@/components/site/AuthorByline";
import { ArticleServiceCta } from "@/components/site/ArticleServiceCta";

import { ReadingProgress } from "@/components/site/wow/ReadingProgress";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { useReadGoal } from "@/hooks/useReadGoal";
import { article, breadcrumbs, speakable, qaPage, person, aeoGeo } from "@/lib/seo/schema";
import { abs , SOCIAL_IMAGE_URL} from "@/lib/seo/constants";

export const Route = createFileRoute("/kunskap_/$slug")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData({
      queryKey: ["blog-post", params.slug],
      queryFn: () => getBlogPost({ data: { slug: params.slug } }),
    });
    if (!data.post) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    const title = post?.seo_title || post?.title || "Artikel — Fenny";
    const description =
      post?.seo_description ||
      post?.excerpt ||
      "Fördjupande artiklar från Fenny om filmproduktion, reklamfilm och abonnemangsmodell för B2B-varumärken som vill växa med rörlig bild.";
    const ogImage = SOCIAL_IMAGE_URL;
    return {
      meta: [
        // Google klipper titlar runt 60 tecken. Lägg bara på varumärket när
        // det får plats och inte redan står i titeln.
        { title: /fenny/i.test(title) || title.length > 52 ? title : `${title} | Fenny` },

        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://fenny.se/kunskap/${params.slug}` },
        { property: "og:image", content: ogImage },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: canonicalUrlFor(`/kunskap/${params.slug}`) }],
      scripts: post
        ? [
            { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: `/kunskap/${params.slug}`, name: post.title })) },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                article({
                  headline: post.title,
                  description: post.excerpt,
                  path: `/kunskap/${params.slug}`,
                  image: ogImage,
                  datePublished: post.published_at,
                  dateModified: post.updated_at ?? post.published_at,
                  authorName: post.author_name,
                  authorRole: post.author_role,
                  section: post.category,
                }),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                breadcrumbs([
                  { name: "Hem", path: "/" },
                  { name: "Kunskap", path: "/kunskap" },
                  { name: post.title, path: `/kunskap/${params.slug}` },
                ]),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                speakable(["h1", "[data-speakable]", ".tldr"]),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                person({
                  name: post.author_name,
                  jobTitle: post.author_role,
                  path: `/kunskap/${params.slug}`,
                }),
              ),
            },
            ...(post.title.toLowerCase().startsWith("vad ") ||
            post.title.toLowerCase().startsWith("hur ") ||
            post.title.toLowerCase().includes("kostar")
              ? [
                  {
                    type: "application/ld+json" as const,
                    children: JSON.stringify(
                      qaPage({
                        question: post.title,
                        answer: post.excerpt,
                        path: `/kunskap/${params.slug}`,
                        authorName: post.author_name,
                      }),
                    ),
                  },
                ]
              : []),
          ]
        : [],
    };
  },
  component: ArticlePage,
  notFoundComponent: () => (
    <div className="container-wide pt-40 pb-20">
      <h1 className="text-3xl font-semibold">Artikeln finns inte</h1>
      <p className="mt-3 text-muted-foreground">
        Den här artikeln kan ha tagits bort eller flyttats.
      </p>
      <div className="mt-6">
        <Button to="/kunskap" variant="mint" arrow>
          Till kunskapsbiblioteket
        </Button>
      </div>
    </div>
  ),
  errorComponent: () => (
    <div className="container-wide pt-40 pb-20">
      <h1 className="text-3xl font-semibold">Något gick fel</h1>
      <p className="mt-3 text-muted-foreground">
        Artikeln kunde inte laddas. Försök ladda om sidan.
      </p>
    </div>
  ),
});

function ArticlePage() {
  const { slug } = Route.useParams();
  const fetchPost = useServerFn(getBlogPost);
  const { data } = useSuspenseQuery({
    queryKey: ["blog-post", slug],
    queryFn: () => fetchPost({ data: { slug } }),
  });
  const post = data.post!;
  const related = data.related;
  const date = new Date(post.published_at).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Plausible/GA4 goal: kunskap_read_75 (Spår 7 mätning)
  useReadGoal("kunskap_read_75", 0.75, { slug });



  return (
    <article>
      <ReadingProgress tone="mint" />
      <section className="pt-36 pb-10 md:pt-44">
        <div className="container-wide max-w-3xl">
          <Link
            to="/kunskap"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-mint"
          >
            <ArrowLeft className="h-4 w-4" /> Kunskap
          </Link>
          <div className="mt-6 flex items-center gap-4 text-xs uppercase tracking-[0.18em] text-mint">
            <span>{post.category}</span>
            <span aria-hidden className="text-muted-foreground">·</span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {post.read_minutes} min läsning
            </span>
          </div>
          <h1 className="mt-5 text-balance text-4xl font-semibold leading-[1.05] text-butter md:text-6xl">
            {post.title}
          </h1>
          <p className="mt-6 text-pretty text-lg text-foreground/85 md:text-xl">
            {post.excerpt}
          </p>
          <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-6 text-sm text-muted-foreground">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-mint/15 text-mint">
              ✦
            </div>
            <div>
              <div className="font-medium text-foreground">
                {post.author_name}
              </div>
              <div className="text-xs">
                {post.author_role} · {date}
              </div>
            </div>
          </div>
          <ArticleServiceCta slug={slug} />
        </div>
      </section>


      <Section className="py-8 md:py-12">
        <div className="mx-auto max-w-3xl">
          <ArticleBody body={post.body} />
          <div className="mt-12 border-t border-white/10 pt-8">
            <AuthorByline
              date={post.published_at}
              reviewedDate={post.updated_at ?? undefined}
            />
          </div>
        </div>
      </Section>

      <SignatureCta
        eyebrow="Vill ni att vi gör det här åt er?"
        title="Pulse gör tanken till film. Varje vecka."
        body="Fenny Pulse är ett filmabonnemang för marknadsteam som vill ha löpande, högkvalitativt videoinnehåll utan att själva hålla i taktpinnen."
        tone="mint"
      />

      <Section className="bg-surface/40">
        <div className="mx-auto max-w-3xl">
          <LeadMagnetForm variant="inline" location={`article_${slug}`} />
        </div>
      </Section>

      {related.length > 0 && (
        <Section>
          <div className="mb-10 text-xs uppercase tracking-[0.18em] text-mint">
            Läs mer
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {related.map((p) => (
              <ArticleCard key={p.slug} post={p} />
            ))}
          </div>
        </Section>
      )}
    </article>
  );
}
